import { AppRoutes } from "./routes";
import { ToastContainer } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import "react-toastify/dist/ReactToastify.css";
import Spinner from "./components/Loader/Spinner/Spinner";
import SpinnerTranslucent from "./components/Loader/Spinner/SpinnerTranslucent";
import { setIsLoading, setIsLoadingTranslucent, setAgentRefreshing, triggerAgentRefresh } from "./store/actions";
import { useEffect } from "react";
import { hideConsoleLogs } from "./helpers/utils";
import GoogleAnalytics from "./components/GoogleAnalytics/GoogleAnalytics";
import ThemeSwitcher from "./components/ThemeSwitcher/ThemeSwitcher";
import { initializeTheme } from "./helpers/globalTheme";
import { useLocation } from "react-router-dom";
import CookieConsentBanner from "./components/CookieConsentBanner/CookieConsentBanner";
import { ProgressProvider } from "./contexts/ProgressContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import ProgressWidget from "./components/ProgressWidget/ProgressWidget";
import UploadRefreshWarning from "./components/UploadRefreshWarning/UploadRefreshWarning";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import AIAgentWidget from "./components/AIAgent/AIAgentWidget";
import { useAgentRefresh } from "./hooks/useAgentRefresh";
// import TrialAlertBanner from "./modules/notifications/components/alert/TrialAlertBanner";
import { queryClient } from "./hooks/queryClient"
import { useRouteLogger } from "./services/logger"


function App() {
  // Hide console logs in production
  hideConsoleLogs();

  // Global agent refresh — invalidates React Query caches for all resources across the entire app.
  // Any page using React Query will silently re-fetch when data changes via the agent.
  useAgentRefresh();

  // Logs a PAGE_VIEW on every route change. See src/services/logger.
  useRouteLogger();

  const isLoading = useSelector((state: any) => state.spinner.isLoading);
  const isLoadingTranslucent = useSelector(
    (state: any) => state.spinnerTranslucent.isLoading
  );
  const location = useLocation();

  const dispatch = useDispatch();

  useEffect(() => {
    if (isLoading) {
      if (isLoadingTranslucent) {
        dispatch(setIsLoadingTranslucent(false));
        dispatch(setIsLoading(true));
      }
    }
    if (isLoadingTranslucent) {
      if (isLoading) {
        dispatch(setIsLoadingTranslucent(true));
        dispatch(setIsLoading(false));
      }
    }
  }, [isLoading, isLoadingTranslucent]);

  // Central agent-refresh handler — one place for the whole app.
  // AIChatWindow fires "livis_agent_refresh" when the agent modifies resources.
  // We dispatch to Redux so components useSelector instead of each registering
  // their own window event listener via useAgentRefresh.
  useEffect(() => {
    const handler = (e: Event) => {
      const resources: string[] = (e as CustomEvent).detail?.resources || [];
      if (resources.length) {
        dispatch(setAgentRefreshing(true));
        dispatch(triggerAgentRefresh(resources));
        setTimeout(() => dispatch(setAgentRefreshing(false)), 3000);
      }
    };
    window.addEventListener("livis_agent_refresh", handler as EventListener);
    return () => window.removeEventListener("livis_agent_refresh", handler as EventListener);
  }, [dispatch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      initializeTheme();
    }, 0);

    return () => clearTimeout(timer);
  }, [location.pathname]);

  useEffect(() => {
    const timer = setTimeout(() => {
      initializeTheme();
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // const isAuthPage = location.pathname.includes("/auth/") || location.pathname === "/app/terms-and-conditions";

  return (
    <QueryClientProvider client={queryClient}>
    <ProgressProvider>
      <NotificationProvider> 
        <UploadRefreshWarning />
        <div className="App">
          <GoogleAnalytics />
          <Spinner is_loading={isLoading} />
          <SpinnerTranslucent is_loading={isLoadingTranslucent} />
          {/* UNIVERSAL ALERT BANNER */}
          {/* {!isAuthPage && <TrialAlertBanner />} */}
          <ToastContainer />
          {/* <ThemeSwitcher /> */}
          <AppRoutes />
          <ProgressWidget />
          <AIAgentWidget />
          <CookieConsentBanner />
        </div>
      </NotificationProvider> 
    </ProgressProvider>
    
    </QueryClientProvider>
  );
}

export default App;